import {
	CTF_CARRIER_FACTOR,
	FUEL_BURN_PER_TICK,
	FUEL_MAX,
	FUEL_REGEN_PER_TICK,
	LASER_COOLDOWN_TICKS,
	LASER_LIFETIME_TICKS,
	LASER_RADIUS,
	LASER_SPEED,
	LAUNCH_GAP,
	MISSILE_COOLDOWN_TICKS,
	MISSILE_LIFETIME_TICKS,
	MISSILE_RADIUS,
	MISSILE_SPEED,
	SHIP_MAX_SPEED,
	SHIP_THRUST,
	SHIP_TURN_RATE,
	TURN_LEFT_SIGN,
	TURN_RIGHT_SIGN,
} from "../constants";
import type { ControlState } from "../controls";
import { createLaser, createMissile, type Ship } from "../entities";
import type { SimEvent } from "../events";
import * as DMath from "../math/det-math";
import type { WorldState } from "../state";
import { allocId, findEntity, insertEntity } from "../world";
import { carriedFlag } from "./objectives";

/** Copies each player's controls onto their current ship (legacy InputManager::refresh). */
export function applyControls(world: WorldState, controls: readonly ControlState[]): void {
	for (const player of world.players) {
		if (player.shipId === null) continue;
		const ship = findEntity(world, player.shipId);
		const input = controls[player.index];
		if (ship?.kind !== "ship" || !input) continue;
		if (ship.frozenTicks > 0) {
			// Freeze power-up: the controls are dead; the ship drifts on.
			ship.thrustHeld = input.thrust;
			ship.thrusting = false;
			ship.turning = 0;
			ship.firingLaser = false;
			ship.firingMissile = false;
			continue;
		}
		ship.thrustHeld = input.thrust;
		// I-025: warp never costs fuel, so an empty tank doesn't block thrust while it's active.
		ship.thrusting =
			input.thrust &&
			(!world.rules.fuel ||
				ship.warpTicks > 0 ||
				ship.fuel >= FUEL_BURN_PER_TICK * world.rules.fuelBurnRate);
		ship.turning = input.left ? -1 : input.right ? 1 : 0;
		ship.firingLaser = input.fireLaser;
		ship.firingMissile = input.fireMissile;
	}
}

/**
 * legacy: Ship::tick. Order matters: thrust (with the pre-rotation heading), rotate,
 * charge weapons, fire (laser has priority), then move.
 */
export function tickShip(world: WorldState, ship: Ship, events: SimEvent[]): void {
	if (world.rules.fuel) burnFuel(world, ship);
	if (ship.frozenTicks > 0) ship.frozenTicks--;

	if (ship.thrusting) {
		const warp = ship.warpTicks > 0;
		// Capture the Flag: carrying a flag slows the ship down.
		const load =
			world.objective?.kind === "ctf" && carriedFlag(world, ship.player) ? CTF_CARRIER_FACTOR : 1;
		const thrust = (warp ? SHIP_THRUST * world.rules.warpThrustFactor : SHIP_THRUST) * load;
		const maxSpeed = (warp ? SHIP_MAX_SPEED * world.rules.warpSpeedFactor : SHIP_MAX_SPEED) * load;
		ship.vel.x += DMath.cos(ship.angle) * thrust;
		ship.vel.y += DMath.sin(ship.angle) * thrust;
		const speed = DMath.hypot(ship.vel.x, ship.vel.y);
		if (speed > maxSpeed) {
			ship.vel.x *= maxSpeed / speed;
			ship.vel.y *= maxSpeed / speed;
		}
	}
	if (ship.warpTicks > 0) ship.warpTicks--;

	if (ship.turning === -1) ship.angle += TURN_LEFT_SIGN * SHIP_TURN_RATE;
	else if (ship.turning === 1) ship.angle += TURN_RIGHT_SIGN * SHIP_TURN_RATE;

	ship.laserCharge = Math.min(ship.laserCharge + 1, LASER_COOLDOWN_TICKS);
	ship.missileCharge = Math.min(ship.missileCharge + 1, MISSILE_COOLDOWN_TICKS);
	const overheat = world.rules.laserOverheat;
	if (overheat) coolLaser(world, ship);
	const limitedMissiles = world.rules.missilesPerLife !== null;
	const laserReady = !overheat || !ship.laserOverheated;
	const missileReady = !limitedMissiles || ship.missiles > 0;
	if (ship.firingLaser && laserReady && ship.laserCharge >= LASER_COOLDOWN_TICKS) {
		fire(world, ship, "laser", events);
		if (ship.tripleLaserTicks > 0) {
			const spread = (world.rules.tripleLaserSpreadDeg * Math.PI) / 180;
			fire(world, ship, "laser", events, -spread);
			fire(world, ship, "laser", events, spread);
		}
		ship.laserCharge = 0;
		if (overheat) heatLaser(world, ship, events);
	} else if (ship.firingMissile && missileReady && ship.missileCharge >= MISSILE_COOLDOWN_TICKS) {
		fire(world, ship, "missile", events);
		ship.missileCharge = 0;
		if (limitedMissiles) ship.missiles--;
	}

	if (ship.tripleLaserTicks > 0) ship.tripleLaserTicks--;

	ship.pos.x += ship.vel.x;
	ship.pos.y += ship.vel.y;
}

/** Overheat rule: the laser cools from max heat to cold in `laserCooldownTicks`, always. */
function coolLaser(world: WorldState, ship: Ship): void {
	const { laserHeatMax, laserCooldownTicks } = world.rules;
	ship.laserHeat = Math.max(0, ship.laserHeat - laserHeatMax / laserCooldownTicks);
	if (ship.laserOverheated && ship.laserHeat === 0) ship.laserOverheated = false;
}

/** Overheat rule: each shot adds heat; reaching max heat locks the laser until it is cold. */
function heatLaser(world: WorldState, ship: Ship, events: SimEvent[]): void {
	const { laserHeatMax, laserHeatPerShot } = world.rules;
	ship.laserHeat += laserHeatPerShot;
	if (ship.laserHeat >= laserHeatMax) {
		ship.laserHeat = laserHeatMax;
		ship.laserOverheated = true;
		events.push({ type: "overheated", id: ship.id, player: ship.player });
	}
}

/** Fuel rule: thrusting burns fuel (not while warp is active, I-025); releasing the thrust key lets the tank refill slowly. */
function burnFuel(world: WorldState, ship: Ship): void {
	if (ship.thrusting) {
		if (ship.warpTicks === 0) {
			ship.fuel = Math.max(0, ship.fuel - FUEL_BURN_PER_TICK * world.rules.fuelBurnRate);
		}
	} else if (!ship.thrustHeld) {
		ship.fuel = Math.min(FUEL_MAX, ship.fuel + FUEL_REGEN_PER_TICK * world.rules.fuelRegenRate);
	}
}

/** Tick lifetime (legacy) or flight distance (laser-range rule) for a new laser. */
function laserLife(world: WorldState): [ticksLeft: number | null, rangeLeft: number | null] {
	const range = world.rules.laserRange;
	return range === null ? [LASER_LIFETIME_TICKS, null] : [null, range];
}

/**
 * legacy: WorldObjectsManager::launch. The projectile appears just ahead of the ship's nose
 * and inherits the ship's velocity plus its own launch speed along the ship's heading.
 * `spread` turns a triple-laser side shot away from the heading.
 */
function fire(
	world: WorldState,
	ship: Ship,
	weapon: "laser" | "missile",
	events: SimEvent[],
	spread = 0,
): void {
	const isLaser = weapon === "laser";
	const offset = ship.radius + (isLaser ? LASER_RADIUS : MISSILE_RADIUS) + LAUNCH_GAP;
	const speed = isLaser ? LASER_SPEED : MISSILE_SPEED;
	const angle = ship.angle + spread;
	const cos = DMath.cos(angle);
	const sin = DMath.sin(angle);
	const pos = { x: ship.pos.x + cos * offset, y: ship.pos.y + sin * offset };
	const vel = { x: cos * speed + ship.vel.x, y: sin * speed + ship.vel.y };
	const id = allocId(world);

	const projectile = isLaser
		? createLaser(id, ship.player, pos, vel, angle, ...laserLife(world))
		: createMissile(id, ship.player, pos, vel, angle, MISSILE_LIFETIME_TICKS);
	const homing = projectile.kind === "missile" && ship.homingShots > 0;
	if (homing) {
		ship.homingShots--;
		projectile.homingTicks = world.rules.homingLockTicks;
	}
	insertEntity(world, projectile, events);
	events.push({
		type: "fired",
		player: ship.player,
		weapon,
		projectileId: id,
		pos: { ...pos },
		...(spread !== 0 ? { side: true as const } : {}),
		...(homing ? { homing: true as const } : {}),
	});
}
